import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { trackCount } from '#api/errors.ts';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { SSOError } from '../error/SSOError.tsx';

const MISSING_STATE = Symbol('missing oidc state');

/**
 * This component is rendered when an identity provider redirects the browser to
 * our OIDC callback url.  We send the browser's current address, including params,
 * to the API server, and if auth is successful, we get back a token and find out
 * who the user is.  After that, we can redirect to the app.
 */
export function CallbackOIDC() {
  const [error, setError] = useState<Error | undefined>();
  const [redirect, setRedirect] = useState<string | undefined>();
  const { verifyOIDC, user, clearOIDCFrom } = useAuth();
  const navigate = useNavigate();
  const { mutate: verifyOIDCMutation } = useMutation({
    // A missing state code resolves instead of rejecting, so the global mutation error handler doesn't report it.
    mutationFn: () =>
      verifyOIDC().catch((err: unknown) => {
        if (err instanceof Error && err.message === 'Could not find oidc state code in storage.') {
          return MISSING_STATE;
        }
        throw err;
      }),
    onSuccess: (result: unknown) => {
      // If there's no state code, then send them to the login page.
      // This could happen from crawling, bookmarking this page somehow, or if we can't set to localStorage.
      if (result === MISSING_STATE) {
        trackCount('oidc_callback.missing_state');
        globalThis.location.replace('/');
        return;
      }
      setRedirect(typeof result === 'string' ? result : '/');
    },
    onError: (err: unknown) => {
      if (err instanceof Error) {
        setError(err);
      }
    },
    onSettled: () => {
      // Clean up the sessionStorage state, just to be safe
      clearOIDCFrom();
      // Clear out the OP's hash/params out of the URL, they only work once.
      void navigate(location.pathname, { replace: true });
    },
  });

  useEffect(() => {
    verifyOIDCMutation();
  }, [verifyOIDCMutation]);

  if (error) {
    return <SSOError error={error} />;
  }

  // Once the user is authenticated and we know where to send them, redirect to the app
  if (user && redirect) {
    return <Navigate to={redirect} replace={true} />;
  }

  // No UI to show here while waiting for the oidc token from the api server.
  return null;
}
